// 2D collision on the XZ plane: a circle (player radius) must stay inside the walkable polygon and
// outside the obstacle boxes. Movement is sub-stepped and resolved by pushing the circle out of each
// contact, which slides it along walls. No allocations per call.

import type { BoxObstacle } from '../scene/manifest';

/** Obstacle boxes with a footprint above this fraction of the walkable area are ignored. */
const MAX_BOX_FRACTION = 0.25;

/** What the player needs from a collider (rooms: Collider; the outdoor field: outdoor/field-collider.ts). */
export interface WalkCollider {
  readonly radius: number;
  /** result of the last resolve() or settle() */
  x: number;
  z: number;
  valid(x: number, z: number): boolean;
  resolve(x0: number, z0: number, x1: number, z1: number): void;
  settle(x: number, z: number): void;
}

export class Collider implements WalkCollider {
  private readonly ax: Float64Array;   // edge start
  private readonly az: Float64Array;
  private readonly bx: Float64Array;   // edge end
  private readonly bz: Float64Array;
  private readonly nx: Float64Array;   // inward normal
  private readonly nz: Float64Array;
  private readonly boxes: Float64Array; // minx, minz, maxx, maxz per box
  private readonly nBoxes: number;
  readonly radius: number;
  /** result of the last resolve() */
  x = 0;
  z = 0;

  constructor(walkable: [number, number][], obstacles: BoxObstacle[], radius: number, floorY: number, eyeHeight: number) {
    this.radius = radius;
    const n = walkable.length;
    this.ax = new Float64Array(n); this.az = new Float64Array(n);
    this.bx = new Float64Array(n); this.bz = new Float64Array(n);
    this.nx = new Float64Array(n); this.nz = new Float64Array(n);
    // orientation: signed area in (x, z); the inward normal side depends on it
    let area = 0;
    for (let i = 0; i < n; i++) {
      const a = walkable[i]!, b = walkable[(i + 1) % n]!;
      area += a[0] * b[1] - b[0] * a[1];
    }
    const sign = area > 0 ? 1 : -1;
    for (let i = 0; i < n; i++) {
      const a = walkable[i]!, b = walkable[(i + 1) % n]!;
      this.ax[i] = a[0]; this.az[i] = a[1]; this.bx[i] = b[0]; this.bz[i] = b[1];
      const ex = b[0] - a[0], ez = b[1] - a[1], len = Math.hypot(ex, ez) || 1;
      // left normal of the edge is (-ez, ex); for a positive-area polygon it points inside
      this.nx[i] = (-ez / len) * sign;
      this.nz[i] = (ex / len) * sign;
    }
    // obstacles that block a walking body: skip boxes entirely under the step height or above the head
    // and boxes whose footprint covers a large part of the room: those are bounding boxes of trim that runs
    // around the walls (e.g. a skirting box spanning the whole floor), not furniture, and would trap the player
    let polyArea = 0;
    for (let i = 0; i < n; i++) {
      const a = walkable[i]!, b = walkable[(i + 1) % n]!;
      polyArea += a[0] * b[1] - b[0] * a[1];
    }
    polyArea = Math.abs(polyArea) / 2;
    const blocking = obstacles.filter((o) => {
      if (o.kind !== 'box' || o.max[1] <= floorY + 0.06 || o.min[1] >= floorY + eyeHeight + 0.1) return false;
      const fx = Math.abs(o.max[0] - o.min[0]), fz = Math.abs(o.max[2] - o.min[2]);
      if (polyArea > 0 && fx * fz > polyArea * MAX_BOX_FRACTION) {
        console.warn(`[collision] ignoring obstacle ${o.id ?? ''} (${fx.toFixed(2)} x ${fz.toFixed(2)} m covers ${Math.round((fx * fz / polyArea) * 100)}% of the walkable area)`);
        return false;
      }
      return true;
    });
    this.nBoxes = blocking.length;
    this.boxes = new Float64Array(this.nBoxes * 4);
    blocking.forEach((o, i) => {
      this.boxes.set([Math.min(o.min[0], o.max[0]), Math.min(o.min[2], o.max[2]), Math.max(o.min[0], o.max[0]), Math.max(o.min[2], o.max[2])], i * 4);
    });
  }

  /** Point-in-polygon (even-odd). */
  inside(x: number, z: number): boolean {
    let c = false;
    const n = this.ax.length;
    for (let i = 0; i < n; i++) {
      const xa = this.ax[i]!, za = this.az[i]!, xb = this.bx[i]!, zb = this.bz[i]!;
      if (za > z !== zb > z && x < ((xb - xa) * (z - za)) / (zb - za) + xa) c = !c;
    }
    return c;
  }

  private inBox(x: number, z: number): boolean {
    const b = this.boxes;
    for (let i = 0; i < this.nBoxes; i++) {
      const o = i * 4;
      if (x > b[o]! && x < b[o + 2]! && z > b[o + 1]! && z < b[o + 3]!) return true;
    }
    return false;
  }

  /** Is the circle centre at a valid place (inside the polygon, outside boxes, clear of edges)? */
  valid(x: number, z: number): boolean {
    return this.inside(x, z) && !this.inBox(x, z);
  }

  /** Move from (x0,z0) toward (x1,z1); result in this.x / this.z. */
  resolve(x0: number, z0: number, x1: number, z1: number): void {
    const r = this.radius;
    const dx = x1 - x0, dz = z1 - z0;
    const dist = Math.hypot(dx, dz);
    const steps = Math.max(1, Math.ceil(dist / (r * 0.5)));
    let x = x0, z = z0;
    for (let s = 0; s < steps; s++) {
      const px = x, pz = z;
      x += dx / steps;
      z += dz / steps;
      for (let it = 0; it < 4; it++) {
        let moved = false;
        // polygon edges: keep the centre at least r from every edge, on the inner side
        const n = this.ax.length;
        for (let i = 0; i < n; i++) {
          const xa = this.ax[i]!, za = this.az[i]!;
          const ex = this.bx[i]! - xa, ez = this.bz[i]! - za;
          const len2 = ex * ex + ez * ez || 1;
          let t = ((x - xa) * ex + (z - za) * ez) / len2;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const cx = xa + ex * t, cz = za + ez * t;
          const side = (x - cx) * this.nx[i]! + (z - cz) * this.nz[i]!;
          const ox = x - cx, oz = z - cz;
          const d = Math.hypot(ox, oz);
          if (side < 0 && side > -r && t > 0 && t < 1) {
            // crossed the edge: put it back on the inner side
            x = cx + this.nx[i]! * r; z = cz + this.nz[i]! * r; moved = true;
          } else if (d < r && side >= 0) {
            if (d > 1e-9) { x = cx + (ox / d) * r; z = cz + (oz / d) * r; }
            else { x = cx + this.nx[i]! * r; z = cz + this.nz[i]! * r; }
            moved = true;
          }
        }
        // boxes: push the circle out
        const b = this.boxes;
        for (let i = 0; i < this.nBoxes; i++) {
          const o = i * 4;
          const minx = b[o]!, minz = b[o + 1]!, maxx = b[o + 2]!, maxz = b[o + 3]!;
          const qx = x < minx ? minx : x > maxx ? maxx : x;
          const qz = z < minz ? minz : z > maxz ? maxz : z;
          const ox = x - qx, oz = z - qz;
          const d2 = ox * ox + oz * oz;
          if (d2 >= r * r) continue;
          if (d2 > 1e-12) {
            const d = Math.sqrt(d2);
            x = qx + (ox / d) * r; z = qz + (oz / d) * r;
          } else {
            // centre inside the box: leave by the nearest face
            const l = x - minx, rr = maxx - x, bb = z - minz, tt = maxz - z;
            const m = Math.min(l, rr, bb, tt);
            if (m === l) x = minx - r; else if (m === rr) x = maxx + r; else if (m === bb) z = minz - r; else z = maxz + r;
          }
          moved = true;
        }
        if (!moved) break;
      }
      if (!this.valid(x, z)) { x = px; z = pz; break; }
    }
    this.x = x;
    this.z = z;
  }

  /** Nearest valid position to (x, z) (used for the spawn point). */
  settle(x: number, z: number): void {
    if (!this.valid(x, z)) {
      // walk toward the polygon centroid until valid
      let cx = 0, cz = 0;
      for (let i = 0; i < this.ax.length; i++) { cx += this.ax[i]!; cz += this.az[i]!; }
      cx /= this.ax.length; cz /= this.ax.length;
      for (let k = 1; k <= 20 && !this.valid(x, z); k++) { x += (cx - x) * 0.2; z += (cz - z) * 0.2; }
    }
    this.resolve(x, z, x, z);
  }
}
